// Advance-booking window for flights, shared by every flight date picker (round-trip
// `DateRangeSelectorNew`, one-way `rangePicker`, multi-city `NewDatePicker`) and the
// default-date parsers (`onBoarding.ts`, `extractFlightAndStayDataFromUrl.tsx`) so the
// cap and the computed defaults always use identical month math. Stays are NOT capped.

// Flights can be booked at most this many months in advance (every leg).
export const FLIGHT_MAX_ADVANCE_MONTHS = 11;

// The booking engine enforces the SAME 11-month rule server-side — `POST /be/v3/search/flights`
// answers 400 "Segment entry at index 0 has departure date more than 11 months in the future" —
// but it measures the window from ITS OWN calendar date while everything in this file measures
// from the BROWSER's. Those two dates disagree for a large part of every day: a user east of the
// engine (India, Europe, APAC) has already rolled over to tomorrow while the engine is still on
// today, so a leg sitting exactly ON the browser's cap is one day PAST the engine's cap and the
// search dies as a hard 400 with zero results. Holding the cap back by a day keeps every date we
// generate — and every date a picker will let the user choose — strictly inside the engine's
// window no matter which side of midnight the two clocks are on. Do not set this to 0.
const WINDOW_SAFETY_DAYS = 1;

// Adds `monthsToAdd` calendar months to `date`, keeping the same day-of-month (clamped to
// the last day of the target month, e.g. Jan 31 + 1mo -> Feb 28).
export const addMonthsSameDay = (date: Date, monthsToAdd: number): Date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const targetMonth = new Date(year, month + monthsToAdd, 1);
  const daysInTargetMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
  const targetDay = Math.min(day, daysInTargetMonth);
  return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), targetDay);
};

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// The last calendar DAY any flight leg may depart on, at local midnight: today + 11 months,
// pulled back by the cross-timezone safety day above.
export const getFlightMaxBookingDay = (): Date => {
  const max = addMonthsSameDay(new Date(), FLIGHT_MAX_ADVANCE_MONTHS);
  max.setDate(max.getDate() - WINDOW_SAFETY_DAYS);
  return startOfDay(max);
};

// Same day as `getFlightMaxBookingDay`, at end-of-day so the cap day itself is inclusive for
// react-calendar's `maxDate`.
export const getFlightMaxBookingDate = (): Date => {
  const max = getFlightMaxBookingDay();
  max.setHours(23, 59, 59, 999);
  return max;
};

// Default departure when no usable date was supplied by the caller/URL: the far edge of the
// bookable window (product default — see `onBoarding.ts` / `extractFlightAndStayDataFromUrl.tsx`).
// Deliberately routed through the capped edge so the default can never be the thing that trips
// the engine's window check.
export const getDefaultFlightDepartureDate = (): Date => getFlightMaxBookingDay();

// Pulls a leg date back inside the window, comparing on the calendar day. Dates arriving from a
// deep link (`?departureDate=…&arrivalDate=…`) never pass through a picker, so this is the only
// thing between an out-of-window campaign URL and the engine's 400.
export const clampToFlightBookingWindow = (date: Date): Date => {
  const max = getFlightMaxBookingDay();
  return startOfDay(date) > max ? max : date;
};
